/**
 * The V2 ↔ V3 switch (plan §4.8.2): decides, per signed-in user, whether
 * App.tsx renders V3App or V2App.
 *
 * ENTRY STUB: App.tsx imports this file statically, so it ships in the V2 entry
 * chunk (REG-3 budget ≤ 2 KB together with fork.tsx — for the build that ships, with `VITE_V3_API_BASE` set, too).
 * Keep it tiny and free of other `src/v3` imports and heavy libraries.
 *
 * Resolution (server side in `vivideoV3-me`; mirrored by `resolveUi` for the
 * cache and for builds without the V3 API):
 *   kill → V2; a staff/tester pin; the user's own choice; the default for new
 *   sign-ups; the default for existing users; otherwise V2.
 *
 * Client behaviour:
 *   1. Decide at once from localStorage (`vivideo_v3_config`,
 *      `vivideo_v3_ui:<uid>`), so V2 users see no added latency.
 *   2. `GET /v3/public/ui-config` is read once per page, from the moment the page boots (in parallel with V2's own
 *      sign-in), and cached; `kill: true` forces V2 immediately.
 *   3. When V3 is enabled (or a `?ui=` link asks), `GET /v3/me` returns the
 *      resolved `ui`, which is cached.
 *   4. R2 (the release, Q57): a page waits for the server only when V3 may apply to it — a `?ui=` link, or the config
 *      (cached, or read while the page signed in) says V3 is enabled, and no decision is cached. Then it waits at most
 *      2.5 s (8 s for a `?ui=` link: a cold V3 API can take longer, P10) and falls back to V2 for the rest of the page
 *      life (counted as `UiResolved{fallback}`) — only if nothing has decided meanwhile. With nothing known yet (the
 *      config not cached and not back by the time the user is known) the page shows V2 at once and keeps it; what the
 *      server says applies from the next page. So a slow or failing V3 API never delays or breaks V2.
 *   The config read, `GET /v3/me`, `PUT /v3/me/ui` and the telemetry live in a small lazy chunk
 *   (./switch/remote.ts, P19b + R2 REG-3), loaded only on a build with the V3 API.
 *   `?ui=v3` / `?ui=v2` set the user's choice when the switch is visible, for
 *   staff, and always on preview/local builds. The parameter is never removed.
 *
 * With no V3 API configured (`VITE_V3_API_BASE` unset) a production build makes no request and always resolves V2.
 */
import { useEffect, useSyncExternalStore, type ComponentType } from 'react';
import { useLocation } from 'react-router-dom';

export type UiVariant = 'v2' | 'v3';
export type UiResolution = UiVariant | 'pending';

/** `GET /v3/public/ui-config` (public, CloudFront-cached). */
export interface V3UiConfig {
  enabled: boolean;
  kill: boolean;
  switch_visible: boolean;
  default_ui_new?: UiVariant | null;
  default_ui_existing?: UiVariant | null;
  /** ISO date: sign-ups on or after it get `default_ui_new`. */
  new_since?: string | null;
}

/** The part of `GET /v3/me` the switch needs. */
export interface V3MeUi {
  ui: UiVariant;
  is_staff?: boolean;
  pin?: UiVariant | null;
  choice?: UiVariant | null;
}

/** The page's landing query, read when this stub evaluates: before main.tsx's initAmplitude() scrubs utm_source /
 *  utm_medium / utm_campaign / vv_cta from the URL (QA-A A-8). V3's day-one attribution reads those keys from here. */
export const LANDING_SEARCH = typeof window !== 'undefined' ? window.location.search : '';

export const CONFIG_KEY = 'vivideo_v3_config';
export const uiCacheKey = (uid: string) => `vivideo_v3_ui:${uid}`;
export const choiceKey = (uid: string) => `vivideo_v3_choice:${uid}`;
// (the first-boot deadlines, FIRST_BOOT_TIMEOUT_MS 2.5 s and URL_CHOICE_TIMEOUT_MS 8 s, live with the wait: switch/remote.ts)

/** Preview (`--mode preview`) and local builds. Production is `production`. */
export const isNonProductionBuild = (): boolean => import.meta.env.MODE !== 'production';

/** The V3 API base URL, or null when V3's backend is not configured for this build. */
export function v3ApiBase(): string | null {
  const fromEnv = (import.meta.env.VITE_V3_API_BASE as string | undefined) || '';
  let base = fromEnv;
  if (!base && isNonProductionBuild()) {
    try {
      base = localStorage.getItem('vivideo_v3_api_base') || '';
    } catch {
      base = '';
    }
  }
  return base ? base.replace(/\/+$/, '') : null;
}

/**
 * Pure resolver (SW-1). `kill` → V2; a pin; the user's own choice (only once V3
 * is enabled); the default for new sign-ups; the default for existing users; V2.
 */
export function resolveUi(input: {
  config: V3UiConfig | null;
  pin?: UiVariant | null;
  choice?: UiVariant | null;
  signupDate?: string | null;
}): UiVariant {
  const { config, pin, choice, signupDate } = input;
  if (config?.kill) return 'v2';
  if (pin) return pin;
  if (!config?.enabled) return 'v2';
  if (choice) return choice;
  const isNew =
    !!config.new_since && !!signupDate && new Date(signupDate).getTime() >= new Date(config.new_since).getTime();
  if (isNew && config.default_ui_new) return config.default_ui_new;
  if (!isNew && config.default_ui_existing) return config.default_ui_existing;
  return 'v2';
}

// ---------------------------------------------------------------------------
// Storage helpers (every access guarded: private mode and blocked storage happen)

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
function readChoice(uid: string): UiVariant | null {
  try {
    const v = localStorage.getItem(choiceKey(uid));
    return v === 'v2' || v === 'v3' ? v : null;
  } catch {
    return null;
  }
}


// ---------------------------------------------------------------------------
// Store (one per page; useSyncExternalStore keeps RootRoutes in sync)

interface State {
  uid: string | null;
  config: V3UiConfig | null;
  me: V3MeUi | null;
  /** True after a first-boot timeout, or a page decided before anything was known: V2 for the rest of the page life. */
  fellBack: boolean;
  /** True while the first-boot fetches are in flight with nothing cached. */
  waiting: boolean;
  /** Bumped on every local change so subscribers re-read. */
  rev: number;
  /** Bumped per resolution (a uid appearing); a late answer for an older one is dropped. */
  seq: number;
  /** This page's one read of `GET /v3/public/ui-config` (switch/remote.ts). */
  configRead?: Promise<void>;
  /** V2's "Try the new Vivideo" slot (switch/barHost.tsx), once the network half is loaded. */
  bar?: ComponentType;
}

const state: State = { uid: null, config: readJson<V3UiConfig>(CONFIG_KEY), me: null, fellBack: false, waiting: false, rev: 0, seq: 0 };
const listeners = new Set<() => void>();
const emit = () => {
  state.rev += 1;
  listeners.forEach((l) => l());
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
/** The store, for the lazy network half (switch/remote.ts). */
export const uiStore = { state, emit };

/**
 * May this user's own choice (the `?ui=` links, the switch bar) decide? Always on
 * preview and local builds; in production, like `v3_switch.can_choose` + the
 * resolver on the server: only once V3 is enabled, and only when the switch is
 * visible or the user is staff. (Before R1, staff use server-side pins.)
 */
export function canChoose(config: V3UiConfig | null, me: V3MeUi | null): boolean {
  // (plain `&&` instead of `?.`: this file ships in V2's entry, and the build lowers every `?.` into a longer form)
  if (isNonProductionBuild()) return !(config && config.kill);
  return !!config && !!config.enabled && !config.kill && (!!config.switch_visible || !!(me && me.is_staff));
}

/** The decision for the current user, from what is known right now. */
export function currentUi(): UiResolution {
  const { uid, config, me, fellBack, waiting } = state;
  if (!uid) return 'v2';
  if (config && config.kill) return 'v2';
  if (fellBack) return 'v2';
  if (me && me.pin) return me.pin;
  const choice = readChoice(uid);
  if (choice && canChoose(config, me)) return choice;
  if (me) return me.ui;
  if (waiting) return 'pending';
  return 'v2';
}

/** The network half of the switch: its own lazy `v3-*` chunk, loaded only on a build with the V3 API (REG-3). */
const remote = () => import('./switch/remote');

/** Fire-and-forget client telemetry through the lazy chunk (`POST /v3/telemetry`; console only without the API). */
export function v3Telemetry(kind: string, data: Record<string, unknown> = {}): void {
  if (v3ApiBase() || isNonProductionBuild())
    remote()
      .then((m) => m.reportV3Telemetry(kind, data))
      .catch(() => undefined);
}

// R2: the config is read as the page boots, in parallel with V2's own sign-in, so it is usually known by the time the
// user is (a build without the V3 API folds this away: no chunk, no request)
if (typeof window !== 'undefined' && v3ApiBase())
  remote()
    .then((m) => m.readUiConfig())
    .catch(() => undefined);

/**
 * A uid appeared (sign-in, the `?lt=` one-tap step, a page load): decide from the cache at once. When V3 may apply (a
 * `?ui=` link, or the config says V3 is enabled) with no cached decision, hold rendering ('pending': switch/remote.ts
 * ends it within 2.5 s; 8 s for a `?ui=` link) instead of rendering V2 for one frame and then switching: that flash
 * mounted V2's /subscription, fired a second paywall_shown and its unmount started V2's exit offer (found by P2's J19).
 * R2: otherwise — V3 not enabled, or nothing known yet — V2 at once, for the rest of this page (what the server says
 * applies from the next page): a slow or failing V3 API never delays V2.
 */
export function adoptUid(uid: string, urlChoice: UiVariant | null): void {
  state.uid = uid;
  state.me = readJson<V3MeUi>(uiCacheKey(uid));
  state.waiting = false;
  const c = state.config;
  if (!v3ApiBase() || state.me || state.fellBack) return;
  if (urlChoice || (c && c.enabled && !c.kill)) state.waiting = true;
  else if (currentUi() === 'v2') state.fellBack = true;
}

/**
 * Starts a resolution for `uid`: the cached decision now; the server's answer and the `?ui=` link's choice through the
 * network half (switch/remote.ts) — on preview and local builds without an API too (anyone may choose there).
 */
function resolveFor(uid: string, urlChoice: UiVariant | null): Promise<void> | undefined {
  const seq = ++state.seq;
  adoptUid(uid, urlChoice);
  emit();
  // the network half ends a wait at its deadline (2.5 s / 8 s); this backstop ends it should that chunk never arrive
  if (state.waiting)
    setTimeout(() => {
      if (seq === state.seq && state.waiting) {
        state.fellBack = currentUi() === 'pending';
        state.waiting = false;
        emit();
      }
    }, 9000);
  if (v3ApiBase() || isNonProductionBuild())
    return remote()
      .then((m) => m.refreshUi(uid, urlChoice, seq))
      .catch(() => undefined);
}

/** Test hook: resets the module state (vitest only). */
export function __resetUiVariantForTests(): void {
  state.seq += 1;
  state.uid = null;
  state.config = readJson<V3UiConfig>(CONFIG_KEY);
  state.me = null;
  state.fellBack = false;
  state.waiting = false;
  state.configRead = undefined;
  emit();
}

/** Test hook: drives the store like RootRoutes does, without React (`refresh` resolves once the server has answered). */
export const __uiVariantTestApi = { refresh: async (uid: string, urlChoice: UiVariant | null) => resolveFor(uid, urlChoice), getState: () => state };

/**
 * RootRoutes' hook. Pass the signed-in backend uid (null while signed out).
 * Returns 'pending' only on a first boot that may be V3 with nothing cached, and never longer than 2.5 s (8 s for `?ui=`).
 */
export function useUiVariant(uid: string | null): UiResolution {
  const { search } = useLocation();
  const raw = new URLSearchParams(search).get('ui');
  const urlChoice = raw === 'v2' || raw === 'v3' ? raw : null;
  useSyncExternalStore(subscribe, () => state.rev);
  useEffect(() => {
    if (!uid) {
      if (state.uid !== null) {
        state.uid = null;
        emit();
      }
      return;
    }
    void resolveFor(uid, urlChoice);
    // Only the uid restarts resolution; a `?ui=` link shows its choice below at once, and the network half records it
    // (switch/remote.ts refreshUi, R2 REG-3) as soon as the user may choose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);
  // Before the effect has run for a new uid, decide from the cache synchronously.
  if (uid && state.uid !== uid) adoptUid(uid, urlChoice);
  if (!uid) return 'v2';
  // A `?ui=` link on a build/user that may choose applies on the very first render.
  // (canChoose is false under a kill)
  if (urlChoice && canChoose(state.config, state.me) && !(state.me && state.me.pin)) return urlChoice;
  return currentUi();
}

/**
 * Hook for V2's switch-bar slot (fork.tsx): the slot's component once the network half is loaded (switch/barHost.tsx
 * decides whether the bar shows). A build without the V3 API never loads it: no bar, no request.
 */
export function useSwitchBar(): ComponentType | undefined {
  useSyncExternalStore(subscribe, () => state.rev);
  // preview and local builds without a V3 API still show the bar (anyone may choose there): load the slot on demand
  // (a constant per build: production builds drop this hook call)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  if (isNonProductionBuild()) useEffect(() => void import('./switch/barHost').catch(() => undefined), []);
  return state.bar;
}
